import { useContext, useState } from 'react';

import { Button, useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import {
  DocumentProgressStatus,
  UploadDocumentToRouteOneMutationVariables,
  useRetrieveDealDocsUploadedToR1Query,
  useSetReadyForSignaturesStatusMutation,
  useUploadDocumentToRouteOneMutation,
} from '../../gql/generated/graphql';

import DocumentsTable from '../DocumentsTable/DocumentsTable';
import { parseFilename } from '../MediaCenter/utils';
import Modal from '../shared/Modal';

import { PermissionEnum } from '../../constants/permissions';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { logger } from '../../libs/Logger';
import { AbilityContext } from '../../libs/contextLib';
import {
  isDocumentProgressSent,
  isDocumentProgressUploaded,
  isDocumentProgressValidated,
} from '../../utils/routeOne';

interface SignatureUploadModalButtonProps {
  width?: string;
}

const SignatureUploadModalButton = ({ width }: SignatureUploadModalButtonProps) => {
  const {
    deal,
    dispatch,
    documentMedia: { signatureFileList },
  } = useContext(DealContext);
  const abilities = useContext(AbilityContext);

  const [selectedFilesIndexes, setSelectedFilesIndexes] = useState<number[]>([]);
  const [numDocsInR1, setNumDocsInR1] = useState<number>(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  // isUploading instead of the mutation loading state because is uploading individual files
  const [isUploading, setIsUploading] = useState(false);

  const isDisabled =
    (!isDocumentProgressValidated(deal.document_progress_status) ||
      !isDocumentProgressUploaded(deal.document_progress_status) ||
      isDocumentProgressSent(deal.document_progress_status)) &&
    !abilities.has(PermissionEnum.SuperUser) &&
    !abilities.has(PermissionEnum.FundingManager);
  const buttonText = 'UPLOAD';

  const [uploadDocToR1] = useUploadDocumentToRouteOneMutation();
  const [setReadyForSignaturesStatus] = useSetReadyForSignaturesStatusMutation();

  // Check to see how many documents are in R1
  const { loading: retrievingDocsUploadedToR1 } = useRetrieveDealDocsUploadedToR1Query({
    skip: !isOpen || deal.id == null,
    variables: { dealId: deal.id ?? -1 },
    fetchPolicy: 'no-cache',
    onCompleted(newData) {
      // don't count the credit app or E-Contract because they are R1 generated
      setNumDocsInR1(
        newData?.retrieveDealDocsUploadedToR1?.filter((doc) => doc?.documentType === 'Other')
          .length ?? 0,
      );
    },
    onError: (error) => {
      logger.error('DocumentsTable.tsx', '', signatureFileList, error);
    },
  });

  const handleUpload = async () => {
    const dealId = deal.id;
    if (dealId == null || !deal.has_funding_jacket) {
      toast.error('This deal has not been submitted to Route One yet');
      return;
    }

    onClose();
    setIsUploading(true);

    const failedFileIndexes: number[] = [];

    const docPromises = signatureFileList
      .filter((_, index) => selectedFilesIndexes.includes(index))
      .map(async (m, selectedFileIndex) => {
        const { friendlyName } = parseFilename(m?.key ?? '');
        const variables: UploadDocumentToRouteOneMutationVariables = {
          dealId,
          doc: {
            key: m.key ?? '',
            name: friendlyName,
          },
          withCobuyer: !!deal.cobuyer,
        };

        try {
          if (variables.doc.key === '') {
            throw new Error('File key is missing');
          }

          const result = await uploadDocToR1({
            variables,
          });

          if (result.data?.uploadDocumentToRouteOne) {
            toast.success(`${friendlyName} uploaded successfully`);
          } else {
            failedFileIndexes.push(selectedFilesIndexes[selectedFileIndex]);
            logger.error(
              'SignatureUpload.tsx',
              'uploadDocumentToRouteOne',
              variables,
              'Unknown error',
            );
            toast.error(`${friendlyName} failed to upload: Unknown error. Please try again.`, {
              autoClose: false,
            });
            throw new Error('Unknown error');
          }
        } catch (e) {
          failedFileIndexes.push(selectedFilesIndexes[selectedFileIndex]);
          logger.error('SignatureUpload.tsx', 'uploadDocumentToRouteOne', variables, e);
          const error = e as Error;
          toast.error(`${friendlyName} failed to upload: ${error.message}.  Please try again.`, {
            autoClose: false,
          });
          throw Error(error.message);
        }
      });

    const results = await Promise.allSettled(docPromises);
    const someDocsUploaded = results.some((r) => r.status === 'fulfilled');
    if (someDocsUploaded) {
      const result = await setReadyForSignaturesStatus({
        variables: {
          dealId,
          documentProgressStatus: deal.document_progress_status,
        },
      });
      if (result.data?.setReadyForSignaturesStatus) {
        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: { document_progress_status: DocumentProgressStatus.ReadyForSignatures },
        });
      }
    }

    setSelectedFilesIndexes(failedFileIndexes);
    setIsUploading(false);
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        w={width}
        onClick={onOpen}
        isDisabled={isDisabled}
        isLoading={isUploading}
        loadingText={buttonText}
      >
        {buttonText}
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title="Upload For Signatures"
        leftButtons={
          <Button variant="warning" onClick={onClose}>
            CANCEL
          </Button>
        }
        rightButtons={
          <Button
            isDisabled={
              (deal.has_funding_jacket && retrievingDocsUploadedToR1) ||
              selectedFilesIndexes.length === 0
            }
            onClick={handleUpload}
          >
            {retrievingDocsUploadedToR1
              ? 'FETCHING'
              : `UPLOAD (${selectedFilesIndexes.length}) DOCUMENTS`}
          </Button>
        }
      >
        <DocumentsTable
          infoText="Select the documents you would like to upload for digital signing."
          files={signatureFileList}
          selectedFilesIndexes={selectedFilesIndexes}
          setSelectedFilesIndexes={setSelectedFilesIndexes}
          modal="upload"
          numDocsInProvider={numDocsInR1}
        />
      </Modal>
    </>
  );
};

export default SignatureUploadModalButton;
